import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface TopSheetProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

export function TopSheet({ open, onClose, children }: TopSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Trap focus when open
  useEffect(() => {
    if (!open) return;
    const firstFocusable = sheetRef.current?.querySelector<HTMLElement>("button, [tabindex]");
    firstFocusable?.focus();

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="fixed inset-0 z-[200] transition-opacity duration-250"
        style={{
          background: "rgba(15, 23, 42, 0.4)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
        }}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className="fixed top-0 left-1/2 z-[210] w-full max-w-[430px] bg-white rounded-b-[20px] shadow-[0_8px_32px_rgba(0,0,0,0.12)] flex flex-col"
        style={{
          maxHeight: "92dvh",
          transform: `translateX(-50%) translateY(${open ? "0" : "-100%"})`,
          transition: "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)",
        }}
      >
        {children}
      </div>
    </>
  );
}

/* ═══════════ Smart Sheet Data ═══════════ */

const FOUND_ITEMS = [
  { icon: "🔧", label: "Dirty filter", key: "dirty_filter" },
  { icon: "🌀", label: "Dirty coils", key: "dirty_coils" },
  { icon: "💧", label: "Refrigerant leak", key: "refrigerant_leak" },
  { icon: "⚖️", label: "Charge issue", key: "charge_issue" },
  { icon: "❄️", label: "Frozen lines", key: "frozen_lines" },
  { icon: "⚡", label: "Electrical", key: "electrical_issue" },
  { icon: "🌪️", label: "Fan/blower", key: "fan_blower_issue" },
  { icon: "⚙️", label: "Compressor", key: "compressor_issue" },
  { icon: "🌡️", label: "Thermostat", key: "thermostat_controls" },
  { icon: "🚿", label: "Drainage", key: "drainage_problem" },
  { icon: "🔊", label: "Noise/vibration", key: "noise_vibration" },
  { icon: "✏️", label: "Other…", key: "other_found" },
] as const;

const ACTION_ITEMS = [
  { icon: "🔧", label: "Changed filter", key: "filter_service" },
  { icon: "🌀", label: "Cleaned coils", key: "cleaned_coils" },
  { icon: "⛽", label: "Recharged refrigerant", key: "recharged_refrigerant", hasDetail: true },
  { icon: "💧", label: "Repaired leak", key: "repaired_leak" },
  { icon: "🔩", label: "Replaced component", key: "replaced_component" },
  { icon: "🚿", label: "Cleared drain", key: "cleared_drain" },
  { icon: "🌡️", label: "Adjusted thermostat", key: "adjusted_thermostat" },
  { icon: "⚡", label: "Tightened electrical", key: "tightened_electrical" },
  { icon: "🔄", label: "Reset system", key: "reset_power_cycle" },
  { icon: "📦", label: "Ordered part", key: "ordered_part" },
  { icon: "✅", label: "No action needed", key: "no_action" },
  { icon: "✏️", label: "Other…", key: "other_action" },
] as const;

const REFRIGERANT_TYPES = ["R-410A", "R-22", "R-454B", "R-32", "Other"] as const;

type FeedbackValue = "confirmed" | "not-found" | "not-sure" | null;

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 mt-5 mb-2.5 first:mt-0">
      <span className="text-[11px] font-bold uppercase tracking-[0.6px] text-[var(--orb-text-tertiary)]">{children}</span>
      <span className="flex-1 h-px bg-[var(--orb-border)]" />
    </div>
  );
}

function Chip({ icon, label, selected, color, onClick }: { icon: string; label: string; selected: boolean; color: "blue" | "green"; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 px-3.5 py-2.5 rounded-full border-[1.5px] text-sm font-medium min-h-[var(--touch-target)] transition-all active:scale-[0.97] select-none",
        selected
          ? color === "green"
            ? "border-[var(--orb-green)] bg-[var(--orb-green-bg)] text-[var(--orb-green)]"
            : "border-[var(--orb-purple)] bg-[var(--orb-purple-light)] text-[var(--orb-purple)]"
          : "border-[var(--orb-border)] bg-white text-[var(--orb-text-primary)]"
      )}
    >
      <span className="text-sm">{icon}</span>{label}
    </button>
  );
}

/** Full Site Visit Report — rendered inside a TopSheet */
export function RecordSiteVisitTopSheet({
  open,
  onClose,
  equipmentName,
  activeIssues = [],
  externalFeedback,
  onExternalFeedbackChange,
}: {
  open: boolean;
  onClose: () => void;
  equipmentName?: string;
  activeIssues?: { id: string; name: string }[];
  externalFeedback?: Record<string, FeedbackValue>;
  onExternalFeedbackChange?: (issueId: string, value: FeedbackValue) => void;
}) {
  const [selectedFound, setSelectedFound] = useState<Set<string>>(new Set());
  const [selectedActions, setSelectedActions] = useState<Set<string>>(new Set());
  const [rechargeDetail, setRechargeDetail] = useState({ lbs: "", type: "" });
  const [isRecording, setIsRecording] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleFound = (key: string) => {
    setSelectedFound((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };
  const toggleAction = (key: string) => {
    setSelectedActions((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const handleSubmit = () => {
    setSubmitted(true);
    toast.success("Site visit logged");
  };

  const handleClose = () => {
    if (submitted) {
      setSubmitted(false);
      setSelectedFound(new Set());
      setSelectedActions(new Set());
      setRechargeDetail({ lbs: "", type: "" });
    }
    onClose();
  };

  if (submitted) {
    return (
      <TopSheet open={open} onClose={handleClose}>
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <div className="w-[88px] h-[88px] rounded-full bg-[var(--orb-green-bg)] flex items-center justify-center text-[44px] mb-6">✓</div>
          <h2 className="text-2xl font-bold mb-2">Visit Logged</h2>
          <p className="text-sm text-[var(--orb-text-secondary)] mb-6">Nice work. Your report is saved.</p>
          <button onClick={handleClose} className="px-10 py-4 rounded-xl bg-[var(--orb-purple)] text-white text-base font-semibold">Back to Asset</button>
        </div>
      </TopSheet>
    );
  }

  return (
    <TopSheet open={open} onClose={handleClose}>
      {/* Header */}
      <div className="px-[var(--page-padding)] pt-5 pb-4 border-b border-[var(--orb-border)] shrink-0 flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold text-[var(--orb-text-primary)]">Site Visit Report</h2>
          <p className="text-[13px] text-[var(--orb-text-secondary)] mt-1">{equipmentName || "Equipment"} · On site now</p>
        </div>
        <button onClick={handleClose} className="w-8 h-8 rounded-full border border-[var(--orb-border)] bg-white flex items-center justify-center shrink-0 ml-3 active:bg-[var(--orb-gray-bg)] transition-colors" aria-label="Close">
          <X size={16} className="text-[var(--orb-text-secondary)]" />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-[var(--page-padding)] py-4">
        {/* Voice note — top for quick access */}
        <SectionLabel>🎙️ Voice Note</SectionLabel>
        <button
          onClick={() => setIsRecording(!isRecording)}
          className={cn(
            "w-full flex items-center gap-3.5 p-4 rounded-[14px] border-[1.5px] border-dashed transition-all",
            isRecording ? "border-red-500 border-solid bg-red-50" : "border-[var(--orb-border)] active:border-[var(--orb-purple)] active:bg-[var(--orb-gray-bg)]"
          )}
        >
          <div className="w-11 h-11 rounded-xl bg-[var(--orb-purple-light)] flex items-center justify-center shrink-0 text-xl">{isRecording ? "⏹️" : "🎤"}</div>
          <div className="text-left">
            <div className="text-[15px] font-semibold text-[var(--orb-text-primary)]">{isRecording ? "Recording…" : "Add voice note"}</div>
            <div className="text-xs text-[var(--orb-text-tertiary)] mt-0.5">{isRecording ? "Tap to stop" : "Tap to record — optional"}</div>
          </div>
        </button>

        {/* Issue Assessment */}
        {activeIssues.length > 0 && (
          <>
            <SectionLabel>Issue Assessment</SectionLabel>
            {activeIssues.map((issue) => {
              const fb = externalFeedback?.[issue.id] ?? null;
              const chips: { key: FeedbackValue; label: string; symbol: string; active: string }[] = [
                { key: "confirmed", label: "Confirmed", symbol: "✓", active: "border-[var(--orb-green)] bg-[var(--orb-green-bg)] text-[var(--orb-green)]" },
                { key: "not-found", label: "Not Found", symbol: "✕", active: "border-[var(--orb-gray)] bg-[var(--orb-gray-bg)] text-[var(--orb-gray)]" },
                { key: "not-sure", label: "Not Sure", symbol: "?", active: "border-[var(--orb-amber)] bg-[var(--orb-amber-bg)] text-[var(--orb-amber)]" },
              ];
              return (
                <div key={issue.id} className="border border-[var(--orb-border)] rounded-xl mb-2.5 overflow-hidden">
                  <div className="flex items-center gap-2.5 px-4 py-3.5">
                    <div className="w-9 h-9 rounded-full bg-[var(--orb-amber-bg)] flex items-center justify-center text-base shrink-0">⚠️</div>
                    <span className="text-[15px] font-semibold flex-1">{issue.name}</span>
                  </div>
                  <div className="flex gap-1 px-3 pb-3">
                    {chips.map((c) => (
                      <button key={c.key} onClick={() => onExternalFeedbackChange?.(issue.id, c.key)}
                        className={cn("flex-1 py-2.5 px-1.5 rounded-lg border-[1.5px] text-xs font-semibold text-center min-h-[var(--touch-target)] flex items-center justify-center transition-all",
                          fb === c.key ? c.active : "border-[var(--orb-border)] bg-white text-[var(--orb-text-secondary)]"
                        )}>{c.symbol} {c.label}</button>
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* What I Found */}
        <SectionLabel>🔍 What I Found</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {FOUND_ITEMS.map((item) => (
            <Chip key={item.key} icon={item.icon} label={item.label} selected={selectedFound.has(item.key)} color="blue" onClick={() => toggleFound(item.key)} />
          ))}
        </div>

        {/* What I Did */}
        <SectionLabel>🛠️ What I Did</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {ACTION_ITEMS.map((item) => (
            <Chip key={item.key} icon={item.icon} label={item.label} selected={selectedActions.has(item.key)} color="green" onClick={() => toggleAction(item.key)} />
          ))}
        </div>

        {/* Recharge detail */}
        {selectedActions.has("recharged_refrigerant") && (
          <div className="mt-2 p-3 bg-[var(--orb-purple-light)] border border-[var(--orb-border)] rounded-lg">
            <p className="text-xs font-semibold text-[var(--orb-text-secondary)] mb-2">RECHARGE DETAILS</p>
            <div className="flex items-center gap-2">
              <input type="number" inputMode="decimal" placeholder="lbs" value={rechargeDetail.lbs}
                onChange={(e) => setRechargeDetail((d) => ({ ...d, lbs: e.target.value }))}
                className="w-20 p-2.5 border border-[var(--orb-border)] rounded-lg text-base bg-white focus:outline-none focus:border-[var(--orb-purple)] min-h-[var(--touch-target)]" />
              <span className="text-sm text-[var(--orb-text-tertiary)]">lbs of</span>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {REFRIGERANT_TYPES.map((type) => (
                <button key={type} onClick={() => setRechargeDetail((d) => ({ ...d, type }))}
                  className={cn("px-3 py-2 rounded-full border-[1.5px] text-xs font-semibold transition-all min-h-[40px]",
                    rechargeDetail.type === type ? "border-[var(--orb-purple)] bg-[var(--orb-purple-light)] text-[var(--orb-purple)]" : "border-[var(--orb-border)] bg-white text-[var(--orb-text-secondary)]"
                  )}>{type}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Submit bar */}
      <div className="shrink-0 bg-white border-t border-[var(--orb-border)] p-3 px-[var(--page-padding)]">
        <button onClick={handleSubmit} className="w-full py-4 rounded-xl bg-[var(--orb-purple)] text-white text-base font-semibold min-h-[52px] active:opacity-90 transition-all">
          Log Site Visit
        </button>
      </div>
    </TopSheet>
  );
}
